
import React from 'react';
import { QrCode, User, School } from 'lucide-react';
import { StudentData } from './types';
import { 
  IdCardConfig, 
  getIdDesign, 
  ColorTheme 
} from './IdCardTypes';
import { CardBase, BackgroundPattern } from './IdCardComponents';
import { calculateSmartExpiry, formatDisplayDate } from './IdCardUtils';

interface IdCardRendererProps {
  data: StudentData;
  config: IdCardConfig;
  side: 'front' | 'back';
}

export const IdCardRenderer: React.FC<IdCardRendererProps> = ({ data, config, side }) => {
  const { layout, theme: baseTheme } = getIdDesign(config.designId);
  const theme: ColorTheme = {
      ...baseTheme,
      primary: config.customPrimaryColor || baseTheme.primary,
      text: config.customTextColor || baseTheme.text,
      textSecondary: config.customTextColor || baseTheme.textSecondary
  };

  const calculatedExpiry = calculateSmartExpiry(data.gradeLevel, data.academicYear, data.programDuration);
  const issuedDate = formatDisplayDate(data.admissionDate || data.academicYear);
  const logoUrl = data.headerSettings?.logo?.url;
  const classDisplay = [data.gradeLevel, data.stream].filter(Boolean).join(" ");

  // Helper Components
  const Photo = ({ className = "" }) => (
    <div className={`bg-slate-200 overflow-hidden relative ${className}`}>
        {data.photoUrl ? (
            <img src={data.photoUrl} alt="Student" className="w-full h-full object-cover" />
        ) : (
            <div className="w-full h-full flex flex-col items-center justify-center bg-slate-100 text-slate-300">
                <User size={16} />
            </div>
        )}
    </div>
  );

  const Logo = ({ size = 20, className = "" }) => (
    <div className={`flex items-center justify-center shrink-0 ${className}`}>
        {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
        ) : (
            <School size={size} className={layout === 'Full' || layout === 'Sidebar' || layout === 'University' ? "text-white" : "text-slate-800"} />
        )}
    </div>
  );

  const DataField = ({ label, value, className = "" }: { label: string, value?: string, className?: string }) => (
    <div className={className}>
        <div className="uppercase font-bold opacity-50 text-[6px] mb-0.5" style={{ color: 'currentColor' }}>{label}</div>
        <div className="font-bold text-[8px] leading-tight" style={{ color: 'currentColor' }}>{value || '-'}</div>
    </div>
  );

  const PrincipalSignature = () => (
    <div className="flex flex-col items-start mt-2">
        <div className="h-3 w-16 border-b border-slate-300 mb-0.5"></div>
        <div className="text-[5px] uppercase font-bold text-slate-400">Principal Signature</div>
    </div>
  );

  const PropertyDisclaimer = () => (
    <p className="text-[5px] text-slate-600 leading-tight mt-3 text-justify">
        This card is the property of {data.schoolName}. It must be presented upon request and is non-transferable.
    </p>
  );

  const ReturnInstructions = () => (
    <div className="text-[7px] leading-normal text-left" style={{ color: theme.text }}>
        <div className="font-bold uppercase text-[7px] mb-1 pt-1 break-words" style={{ color: theme.primary, lineHeight: '1.2' }}>If Found, Please Return To:</div>
        <div className="font-bold">{data.schoolName}</div>
        <div className="opacity-80 mt-0.5">
            {data.schoolAddress || 'Administrative Office'}<br/>
            {data.schoolContact || 'Contact Office'}
        </div>
    </div>
  );

  if (side === 'front') {
    const renderFront = () => {
      switch(layout) {
        case 'University': return (
            <div className="flex flex-col h-full relative z-10">
                <div className="h-[14mm] w-full flex items-center px-4 gap-3 relative z-10" style={{ backgroundColor: theme.primary }}>
                    <div className="bg-white/20 p-1 rounded-full shrink-0"><Logo className="w-7 h-7 text-white" size={28} /></div>
                    <div className="flex-1"><h1 className="text-white font-serif font-bold text-[12px] uppercase leading-none tracking-wide">{data.schoolName}</h1></div>
                </div>
                <div className="h-[5mm] w-full flex items-center justify-center relative z-10" style={{ backgroundColor: theme.secondary }}>
                    <span className="text-white text-[8px] font-bold uppercase tracking-[0.2em]">Student Identity Card</span>
                </div>
                <div className="flex-1 flex p-4 gap-4 relative z-10">
                    <div className="w-[24mm] h-[30mm] shrink-0 border-2 bg-slate-50 relative z-10 shadow-sm" style={{ borderColor: theme.primary }}><Photo className="w-full h-full" /></div>
                    <div className="flex-1 flex flex-col pt-1">
                        <h2 className="text-[18px] font-serif font-bold uppercase leading-tight mb-1" style={{ color: theme.text }}>{data.name}</h2>
                        <div className="grid grid-cols-1 gap-y-2 mt-2 text-[9px]">
                            <div><div className="uppercase font-bold opacity-50 text-[6px]" style={{ color: theme.textSecondary }}>ID Number</div><div className="font-mono font-bold" style={{ color: theme.primary }}>{data.studentId}</div></div>
                            <div className="grid grid-cols-2 gap-2">
                                <div><div className="uppercase font-bold opacity-50 text-[6px]" style={{ color: theme.textSecondary }}>Class/Stream</div><div className="font-bold" style={{ color: theme.text }}>{classDisplay || '-'}</div></div>
                                <div><div className="uppercase font-bold opacity-50 text-[6px]" style={{ color: theme.textSecondary }}>Issued</div><div className="font-bold" style={{ color: theme.text }}>{issuedDate}</div></div>
                                <div><div className="uppercase font-bold opacity-50 text-[6px]" style={{ color: theme.textSecondary }}>Expires</div><div className="font-bold border-b border-dotted" style={{ color: theme.text, borderColor: theme.text }}>{calculatedExpiry}</div></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="h-[6mm] mx-4 mb-3 border-t border-slate-200 flex items-center justify-between relative z-10">
                    <div className="h-3 w-32 bg-slate-800 opacity-80" style={{ maskImage: 'repeating-linear-gradient(90deg, black, black 1px, transparent 1px, transparent 3px)' }}></div>
                    <div className="text-[6px] font-bold opacity-50 uppercase tracking-wider text-right">Authorized</div>
                </div>
            </div>
        );
        case 'Sidebar': return (
            <div className="flex h-full relative z-10">
                <div className="w-[30%] h-full flex flex-col items-center pt-5 pb-4 px-2 relative z-10 shadow-sm" style={{ backgroundColor: theme.primary }}>
                    <div className="w-[20mm] h-[26mm] bg-white p-1 rounded-sm shadow-md mb-3"><Photo className="w-full h-full rounded-sm" /></div>
                    <div className="mt-auto flex flex-col items-center"><Logo className="w-8 h-8 text-white/90 mb-2" size={24} /><div className="text-[7px] text-white/80 font-mono tracking-wider">{data.studentId}</div></div>
                </div>
                <div className="flex-1 p-5 flex flex-col justify-center relative z-10">
                    <div className="border-b-2 pb-3 mb-3" style={{ borderColor: theme.accent }}><h1 className="font-bold text-[12px] uppercase leading-tight tracking-tight" style={{ color: theme.primary }}>{data.schoolName}</h1><div className="text-[7px] font-bold uppercase tracking-widest mt-1 opacity-60" style={{ color: theme.text }}>Student Identity Card</div></div>
                    <div>
                        <h2 className="text-[16px] font-bold uppercase leading-tight mb-3" style={{ color: theme.text }}>{data.name}</h2>
                        <div className="grid grid-cols-2 gap-3">
                            <DataField label="Class / Stream" value={classDisplay} />
                            <DataField label="Issued" value={issuedDate} />
                            <DataField label="Expires" value={calculatedExpiry} />
                        </div>
                    </div>
                </div>
            </div>
        );
        case 'Hero': return (
             <div className="flex flex-col h-full relative z-10">
                 <div className="h-[14mm] w-full flex items-center px-5 gap-3 relative z-10" style={{ backgroundColor: theme.primary }}><Logo className="w-8 h-8 bg-white/10 rounded p-1 text-white" /><div className="flex-1"><h1 className="text-white font-bold text-[11px] uppercase leading-tight">{data.schoolName}</h1><div className="text-white/80 text-[6px] uppercase tracking-widest font-bold">Student Identity Card</div></div></div>
                 <div className="flex-1 flex p-5 gap-4 relative z-10">
                     <div className="w-[22mm] h-[28mm] shrink-0 border border-slate-200 rounded-sm shadow-sm overflow-hidden relative z-10"><Photo className="w-full h-full" /></div>
                     <div className="flex-1 pt-1 relative z-10">
                        <h2 className="text-[15px] font-bold uppercase leading-tight mb-3" style={{ color: theme.text }}>{data.name}</h2>
                        <div className="grid grid-cols-2 gap-y-2 gap-x-1">
                            <DataField label="ID Number" value={data.studentId} />
                            <DataField label="Issued" value={issuedDate} />
                            <DataField label="Expires" value={calculatedExpiry} />
                            <div className="col-span-2"><DataField label="Class/Stream" value={classDisplay} /></div>
                        </div>
                    </div>
                 </div>
                 <div className="h-1 w-full relative z-10" style={{ backgroundColor: theme.secondary }}></div>
             </div>
        );
        case 'Full': return (
             <div className="flex h-full">
                 <div className="w-[38%] h-full flex flex-col p-4 relative overflow-hidden" style={{ backgroundColor: theme.primary }}>
                     <div className="w-[20mm] h-[25mm] bg-white/10 rounded border border-white/20 self-center mb-3 overflow-hidden backdrop-blur-sm relative z-10"><Photo className="w-full h-full" /></div>
                     <div className="mt-auto text-center relative z-10"><h2 className="text-white text-[12px] font-bold uppercase leading-tight mb-1">{data.name.split(' ')[0]}</h2><p className="text-white/70 text-[8px] uppercase">{data.studentId}</p></div>
                 </div>
                 <div className={`flex-1 flex flex-col p-4 justify-between relative`}>
                     <div className="flex items-start justify-between relative z-10"><div className="flex-1"><h1 className="font-bold text-[10px] uppercase leading-tight mb-0.5" style={{ color: theme.primary }}>{data.schoolName}</h1><div className="text-[5px] font-bold uppercase tracking-widest mb-1 opacity-60" style={{ color: theme.text }}>Student Identity Card</div><div className="h-0.5 w-8 bg-slate-200" style={{ backgroundColor: theme.accent }}></div></div><Logo className="w-6 h-6" /></div>
                     <div className="relative z-10">
                        <h2 className="text-[14px] font-bold uppercase leading-tight mb-2" style={{ color: theme.text }}>{data.name}</h2>
                        <div className="grid grid-cols-2 gap-y-2">
                            <DataField label="ID Number" value={data.studentId} />
                            <DataField label="Issued" value={issuedDate} />
                            <DataField label="Expires" value={calculatedExpiry} />
                            <div className="col-span-2"><DataField label="Class" value={classDisplay} /></div>
                        </div>
                     </div>
                 </div>
             </div>
        );
        case 'Diagonal': return (
            <div className="flex h-full overflow-hidden relative z-10 bg-transparent">
                <div className="absolute top-0 left-0 w-[40%] h-full border-r border-slate-100/30" style={{ backgroundColor: config.customBackground ? 'rgba(255,255,255,0.7)' : 'rgba(248, 250, 252, 0.5)' }}></div>
                <div className="absolute top-[-20%] right-[-10%] w-[60%] h-[140%] transform rotate-12 opacity-10" style={{ backgroundColor: theme.accent }}></div>
                <div className="relative z-10 w-full flex p-5 gap-5">
                    <div className="flex flex-col gap-3 w-[24mm]"><div className="w-[24mm] h-[30mm] shadow-md rounded overflow-hidden border border-white"><Photo className="w-full h-full" /></div><div className="flex justify-center"><Logo className="w-6 h-6 opacity-80" /></div></div>
                    <div className="flex-1 flex flex-col justify-center">
                        <div className="mb-4 border-l-2 pl-3" style={{ borderColor: theme.primary }}>
                            <h1 className="font-bold text-[10px] uppercase leading-tight mb-0.5" style={{ color: theme.primary }}>{data.schoolName}</h1>
                            <p className="text-[6px] uppercase tracking-wider" style={{ color: theme.textSecondary }}>Student Identity Card</p>
                        </div>
                        <div>
                            <h2 className="text-[16px] font-bold uppercase leading-tight mb-1" style={{ color: theme.text }}>{data.name}</h2>
                            <div className="flex items-center gap-2 mb-3"><span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: theme.secondary }}>{data.studentId}</span></div>
                            <div className="grid grid-cols-2 gap-2">
                                <DataField label="Class/Stream" value={classDisplay} />
                                <DataField label="Issued" value={issuedDate} />
                                <DataField label="Expires" value={calculatedExpiry} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
        default: return ( // Modern
            <div className="flex flex-col h-full p-5 relative z-10 bg-transparent">
                <div className="flex items-center gap-3 mb-4 border-b border-slate-100 pb-2 relative z-10" style={{ borderColor: theme.accent }}><Logo className="w-8 h-8" size={24} /><div><h1 className="font-bold text-[12px] uppercase leading-tight" style={{ color: theme.primary }}>{data.schoolName}</h1><div className="text-[6px] uppercase tracking-widest mt-0.5 font-bold" style={{ color: theme.textSecondary }}>Student Identity Card</div></div></div>
                <div className="flex gap-5 flex-1 relative z-10">
                    <div className="w-[23mm] h-[29mm] shrink-0 border border-slate-200 shadow-sm rounded-sm overflow-hidden"><Photo className="w-full h-full" /></div>
                    <div className="flex-1 flex flex-col justify-center">
                        <div><h2 className="text-[16px] font-bold leading-tight uppercase mb-1" style={{ color: theme.text }}>{data.name}</h2></div>
                        <div className="grid grid-cols-2 gap-y-2 gap-x-1 mt-3">
                            <DataField label="Student ID" value={data.studentId} />
                            <DataField label="Class/Stream" value={classDisplay} />
                            <DataField label="Issued" value={issuedDate} />
                            <DataField label="Expires" value={calculatedExpiry} />
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1.5" style={{ backgroundColor: theme.primary }}></div>
            </div>
        );
      }
    };

    return (
      <CardBase backgroundImage={config.customBackground} backgroundOpacity={config.patternOpacity}>
        <BackgroundPattern opacity={config.patternOpacity} color={config.patternColor} type={config.pattern} idPrefix="front" />
        {renderFront()}
      </CardBase>
    );
  } else {
    const renderBack = () => {
      switch(layout) {
        case 'University': return (
            <div className="flex flex-col h-full relative z-10">
                <div className="h-[6mm] w-full flex items-center justify-center relative z-10" style={{ backgroundColor: theme.primary }}>
                    <div className="text-white text-[8px] font-bold uppercase tracking-widest">{data.schoolName}</div>
                </div>
                <div className="flex-1 flex flex-col p-4 relative z-10">
                    <div className="flex-1">
                        <ReturnInstructions />
                        <PropertyDisclaimer />
                    </div>
                    <div className="mt-auto flex items-end justify-between border-t border-slate-100 pt-2" style={{ borderColor: theme.accent }}>
                        <PrincipalSignature />
                        <div className="flex flex-col items-end gap-1">
                            <QrCode size={42} className="opacity-90" style={{ color: theme.text }} />
                            <div className="text-[4px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">Product of Sonoma Cloud</div>
                        </div>
                    </div>
                </div>
                <div className="h-[3mm] w-full relative z-10" style={{ backgroundColor: theme.secondary }}></div>
            </div>
        );
        case 'Sidebar': return (
            <div className="flex h-full relative z-10">
                 <div className="w-[12%] h-full relative z-10 flex flex-col items-center py-4 justify-between" style={{ backgroundColor: theme.primary }}>
                    <div className="h-full w-full flex items-center justify-center">
                        <div className="text-[5px] font-bold tracking-widest text-white/40 uppercase whitespace-nowrap" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Property of School</div>
                    </div>
                 </div>
                 <div className="flex-1 p-4 flex flex-col relative z-20">
                     <div className="flex-1 space-y-3">
                        <ReturnInstructions />
                        <PropertyDisclaimer />
                     </div>
                     <div className="mt-auto flex items-end justify-between pt-2 border-t border-slate-100" style={{ borderColor: theme.accent }}>
                        <PrincipalSignature />
                        <div className="flex flex-col items-end gap-1">
                            <QrCode size={40} className="opacity-90" style={{ color: theme.text }} />
                            <div className="text-[4px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">Product of Sonoma Cloud</div>
                        </div>
                     </div>
                 </div>
            </div>
        );
        case 'Hero': return (
            <div className="flex flex-col h-full relative z-10">
                <div className="flex-1 p-5 flex flex-col relative z-20">
                     <div className="flex-1 space-y-3 text-left">
                        <ReturnInstructions />
                        <PropertyDisclaimer />
                     </div>
                    <div className="mt-auto pt-2 border-t border-slate-100 flex items-end justify-between" style={{ borderColor: theme.accent }}>
                         <PrincipalSignature />
                         <div className="flex items-center gap-2">
                             <div className="text-[4px] font-bold text-slate-500 uppercase tracking-widest" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Product of Sonoma Cloud</div>
                             <div className="p-0.5 border border-slate-100 rounded"><QrCode size={42} className="opacity-90" style={{ color: theme.text }} /></div>
                         </div>
                    </div>
                </div>
                <div className="h-1.5 w-full flex items-center justify-center" style={{ backgroundColor: theme.primary }}></div>
            </div>
        );
        case 'Full': return (
             <div className="flex h-full relative z-10">
                 <div className="w-[30%] h-full relative z-10 p-3 flex flex-col items-center justify-end pb-4 text-center" style={{ backgroundColor: theme.primary }}>
                     <QrCode size={48} className="text-white opacity-90 mix-blend-overlay mb-1" />
                     <div className="text-[4px] font-bold text-white/90 uppercase tracking-widest mt-0.5">Product of Sonoma Cloud</div>
                 </div>
                 <div className={`flex-1 p-4 flex flex-col justify-between relative`}>
                      <div className="relative z-10 flex-1 space-y-3">
                          <ReturnInstructions />
                          <PropertyDisclaimer />
                      </div>
                      <div className="relative z-10 mt-auto pt-2 border-t border-slate-100" style={{ borderColor: theme.accent }}>
                          <PrincipalSignature />
                      </div>
                 </div>
             </div>
        );
        case 'Diagonal': return (
            <div className="flex h-full w-full relative overflow-hidden z-10">
                <div className="absolute inset-y-0 right-0 w-[40%] border-l border-slate-200 z-0" style={{ backgroundColor: config.customBackground ? 'rgba(255,255,255,0.9)' : 'rgba(248, 250, 252, 0.8)' }}></div>
                <div className="absolute -bottom-10 -left-10 w-[80%] h-[150%] origin-bottom-left rotate-12 opacity-10 pointer-events-none z-0" style={{ backgroundColor: theme.accent }}></div>
                
                <div className="relative z-10 flex-1 p-5 flex flex-col h-full w-full text-left">
                     <div className="flex-1 flex flex-col space-y-3">
                        <div className="uppercase text-[6px] font-bold tracking-widest border-b pb-1 mb-1" style={{ color: theme.textSecondary, borderColor: theme.accent }}>Property of {data.schoolName}</div>
                        <ReturnInstructions />
                        <PropertyDisclaimer />
                     </div>
                     <div className="flex items-end justify-between mt-auto pt-2 border-t border-slate-100 relative z-10" style={{ borderColor: theme.accent }}>
                         <PrincipalSignature />
                         <div className="flex flex-col items-end gap-1">
                             <div className="p-1 bg-white rounded border border-slate-200">
                                 <QrCode size={38} className="text-slate-800" strokeWidth={1.5} />
                             </div>
                             <div className="text-[4px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">Product of Sonoma Cloud</div>
                         </div>
                     </div>
                </div>
            </div>
        );
        default: return ( // Modern
          <div className="relative z-10 h-full flex flex-col p-5">
              <div className="flex-1 flex flex-col space-y-3 relative z-10 text-left">
                  <div className="uppercase text-[6px] font-bold tracking-widest border-b pb-1 mb-1" style={{ color: theme.textSecondary, borderColor: theme.accent }}>Property of {data.schoolName}</div>
                  <ReturnInstructions />
                  <PropertyDisclaimer />
              </div>
              <div className="flex items-end justify-between mt-auto pt-2 border-t border-slate-100 relative z-10" style={{ borderColor: theme.accent }}>
                      <PrincipalSignature />
                      <div className="flex flex-col items-end gap-1">
                          <div className="p-1 bg-white rounded border border-slate-200">
                              <QrCode size={38} className="text-slate-800" strokeWidth={1.5} />
                          </div>
                          <div className="text-[4px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">Product of Sonoma Cloud</div>
                      </div>
              </div>
          </div>
        );
      }
    };

    return (
      <CardBase backgroundImage={config.customBackground} backgroundOpacity={config.patternOpacity}>
        <BackgroundPattern opacity={config.patternOpacity} color={config.patternColor} type={config.pattern} idPrefix="back" />
        {renderBack()}
      </CardBase>
    );
  }
};
